import { useLocalStorageJSON } from '@highimpact/react-hooks';
import MenuIcon from '@mui/icons-material/Menu';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import assert from 'assert';
import type { Ref } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  getNonOrphanedProjects,
  getSitesFromProject,
} from '../../../store/config';
import { isDataState } from '../../../store/data/state';
import { useAppSelector } from '../../../store/hooks';
import type { HorizontalNavProps } from '../../utils/nav/HorizontalNavComponent';
import StateDecorator from '../../utils/state/StateDecorator';
import ProjectSiteNavigatorComponent from './ProjectSiteNavigatorComponent';
import { useTranslation } from 'react-i18next';

function readCurrentSection(location: ReturnType<typeof useLocation>) {
  const currentSectionResult = location.pathname.match(
    /\/projects\/\d+\/sites\/\d+\/([a-zA-Z-0-9%]+)/
  );
  const result = currentSectionResult ? currentSectionResult[1] : undefined;
  return result ? decodeURIComponent(result) : undefined;
}

function readProjectAndSite(location: ReturnType<typeof useLocation>) {
  const match = location.pathname.match(/\/projects\/(\d+)\/sites\/(\d+)/);
  return {
    projectId: match?.[1] ? Number.parseInt(match[1], 10) : undefined,
    siteId: match?.[2] ? Number.parseInt(match[2], 10) : undefined,
  };
}

const TOP_NAV_PINNED_KEY = 'highimpact:top-nav:is-pinned';
const TOP_NAV_OPEN_TRIGGER_MIN_HEIGHT = 24;
const TOP_NAV_CLOSE_BUFFER = 16;

export interface TopNavProps extends HorizontalNavProps {
  leftElement?: JSX.Element;
  hideSiteNavigator?: boolean;
  hideToggleShowLeftNav?: boolean;
  onToggleShowLeftNav?: () => void;
  barHeight?: number;
  collapsedHeight?: number;
  isCollapsible?: boolean;
  isPinnable?: boolean;
  showCollapsedMenuButton?: boolean;
}

function TopNavComponent(
  {
    leftElement,
    hideSiteNavigator,
    hideToggleShowLeftNav = true,
    children,
    onToggleShowLeftNav,
    barHeight = 64,
    collapsedHeight = 8,
    isCollapsible = true,
    isPinnable = true,
    showCollapsedMenuButton = true,
    side: _side,
    className,
    style,
    id,
    title,
    role,
  }: TopNavProps,
  ref: Ref<HTMLDivElement>
) {
  // use translation from 'menu' namespace
  const { t } = useTranslation('menu');
  const theme = useTheme();
  const location = useLocation();
  const { site_id, project_id } = useParams<{ site_id: string; project_id: string; }>();
  const [isHovered, setIsHovered] = useState(false);
  const [savedPinnedPreference, setSavedPinnedPreference] =
    useLocalStorageJSON<boolean>(TOP_NAV_PINNED_KEY, false);

  const safeBarHeight = useMemo(() => Math.max(1, barHeight), [barHeight]);
  const safeCollapsedHeight = useMemo(
    () => Math.max(0, Math.min(collapsedHeight, safeBarHeight)),
    [collapsedHeight, safeBarHeight]
  );
  const hoverOpenTriggerHeight = useMemo(
    () => Math.max(safeCollapsedHeight, TOP_NAV_OPEN_TRIGGER_MIN_HEIGHT),
    [safeCollapsedHeight]
  );

  const isPinned = isCollapsible && isPinnable && savedPinnedPreference;
  const isOpen = !isCollapsible || isPinned || isHovered;
  const translateY = isOpen ? 0 : safeCollapsedHeight - safeBarHeight;

  useEffect(() => {
    if (!isCollapsible || isPinned) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const shouldOpen = event.clientY <= hoverOpenTriggerHeight;
      const shouldClose = event.clientY > safeBarHeight + TOP_NAV_CLOSE_BUFFER;

      setIsHovered((prev) => {
        if (!prev && shouldOpen) {
          return true;
        }

        if (prev && shouldClose) {
          return false;
        }

        return prev;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isCollapsible, isPinned, hoverOpenTriggerHeight, safeBarHeight]);

  const currentSection = readCurrentSection(location);
  // const { projectId, siteId } = readProjectAndSite(location);
  const projectId = useMemo(() : number | undefined => {
    return project_id ? Number.parseInt(project_id as string, 10) : undefined
  }, [project_id]);
  const siteId = useMemo(() : number | undefined => {
    return site_id ? Number.parseInt(site_id as string, 10) : undefined
  }, [site_id]);

  const projects = useAppSelector(
    (state) => state.data_slice.externals.projects
  );
  const sites = useAppSelector((state) => state.data_slice.externals.sites);
  const project_sites = useAppSelector(
    (state) => state.data_slice.externals.project_sites
  );
  const siteIdsToDashboardIds = useAppSelector(
    (state) => state.data_slice.externals.siteIdsToDashboardIds
  );

  // Stable references to avoir rerendering issues when directly accessing URL
  const projectList = useMemo(
    () =>
      isDataState(projects) && isDataState(project_sites)
        ? getNonOrphanedProjects(projects, project_sites)
        : [],
    [projects, project_sites]
  );

  const siteList = useMemo(
    () => (isDataState(sites) ? Object.values(sites.data) : []),
    [sites]
  );

  const getSiteSubset = useCallback(
    (id: number) => {
      if (!isDataState(projects) || !isDataState(project_sites) || !isDataState(sites))
        return [];
      const project = projects.data[id];
      if (!project) return [];
      return getSitesFromProject(project, project_sites, sites);
    },
    [projects, project_sites, sites]
  );

  return StateDecorator(
    [sites, projects, project_sites, siteIdsToDashboardIds],
    ref,
    () => {
      assert(isDataState(sites));
      assert(isDataState(projects));
      assert(isDataState(project_sites));
      assert(isDataState(siteIdsToDashboardIds));

      return (
        <>
          <Box
            id={id}
            title={title}
            role={role}
            className={className}
            style={style}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: theme.zIndex.appBar + 1,
              height: isOpen ? safeBarHeight : safeCollapsedHeight,
              transition: theme.transitions.create('height', {
                duration: theme.transitions.duration.shortest,
                easing: theme.transitions.easing.sharp,
              }),
              overflow: 'visible',
            }}
          >
            <AppBar
              position="absolute"
              sx={{
                backgroundColor: '#ffffff',
                transform: `translateY(${translateY}px)`,
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shorter,
                  easing: theme.transitions.easing.sharp,
                }),
              }}
              elevation={1}
            >
              <Toolbar
                sx={{
                  justifyContent: 'space-between',
                  minHeight: safeBarHeight,
                  height: safeBarHeight,
                }}
              >
                {hideToggleShowLeftNav ? (
                  leftElement
                ) : (
                  <Stack direction="row" spacing={2}>
                    <IconButton
                      color="inherit"
                      edge="start"
                      onClick={() => onToggleShowLeftNav?.()}
                      sx={{ mr: 2 }}
                    >
                      <MenuIcon />
                    </IconButton>
                    {leftElement}
                  </Stack>
                )}

                {!hideSiteNavigator && (
                  <ProjectSiteNavigatorComponent
                    defaultProjectId={projectId}
                    defaultSiteId={siteId}
                    section={currentSection ?? 'tableau-de-bord'}
                    projectList={projectList}
                    siteList={siteList}
                    getSiteSubset={getSiteSubset}
                    siteIdsToDashboardIds={siteIdsToDashboardIds.data}
                  />
                )}

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                >
                  {isCollapsible && isPinnable && (
                    <Tooltip
                      title={
                        isPinned ? t('Désépingler la barre') : t('Épingler la barre')
                      }
                      placement="bottom"
                      arrow
                    >
                      <IconButton
                        color="inherit"
                        onClick={() =>
                          setSavedPinnedPreference(!savedPinnedPreference)
                        }
                        aria-label={
                          isPinned
                            ? 'Désépingler la barre'
                            : 'Épingler la barre'
                        }
                        aria-pressed={isPinned}
                        size="small"
                      >
                        {isPinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                      </IconButton>
                    </Tooltip>
                  )}
                  <div style={{ justifyContent: 'space-between' }}>
                    {children}
                  </div>
                </Stack>
              </Toolbar>
            </AppBar>
          </Box>

          {isCollapsible &&
            showCollapsedMenuButton &&
            !hideToggleShowLeftNav &&
            !isOpen && (
              <IconButton
                color="primary"
                onClick={() => onToggleShowLeftNav?.()}
                aria-label="Ouvrir le menu latéral"
                sx={{
                  position: 'fixed',
                  top: 4,
                  left: 8,
                  zIndex: theme.zIndex.appBar + 2,
                  backgroundColor: '#ffffff',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    backgroundColor: '#ffffff',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
        </>
      );
    }
  );
}

export default forwardRef(TopNavComponent);
